import { TFunction } from "react-i18next";
import { Flash } from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { SubscriptionBasedModalConfigProps } from "go-core/components/SubscriptionBasedRestriction/hooks/useSubscriptionBasedRestriction";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { apiOrganization as apiSecurityOrganization } from "go-security/services/Api/apiOrganization";
import { OrganizationState } from "go-security/services/organizations/types";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../services/Api/Organization/apiOrganization";
import { EmployeeApi } from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import { getGoAccountsApiURL, getSubscriptionBasedRestrictions } from "../../../../../../../../utils/envutil";

export const onFetchEmployee = async (
	employeeId: string | number,
	setEmployee: (employee: EmployeeApi | undefined) => void,
	addFlash: (_: Flash) => void,
	t: TFunction,
	isCopyMode?: boolean
) => {
	try {
		const params: Record<string, any> = {
			include: "role,role.permissions,workplace,workplaces,workplaces.workplace,custom_fields",
		};
		const res: EmployeeApi = await api.organization().getEmployee(Number(employeeId), params);

		const newEmployeeValues = { ...res };
		if (isCopyMode) {
			newEmployeeValues.name = `${t("common.word.copy")} ${newEmployeeValues.name}`;
			newEmployeeValues.code = "";
			newEmployeeValues.card_code = "";
			newEmployeeValues.contact_phone_number = "";
			newEmployeeValues.contact_email = "";
			newEmployeeValues.address_city = "";
			newEmployeeValues.address_zip_code = "";
			newEmployeeValues.address_street = "";
			newEmployeeValues.address_build_nr = "";
			newEmployeeValues.address_flat_nr = "";
			newEmployeeValues.address_country = "";
			newEmployeeValues.contact_other = "";
		}
		setEmployee(newEmployeeValues);
	} catch (err) {
		handleError.alert(err, addFlash);
	}
};

export const fetchEmployeeConfig = async (
	setCustomFieldsConfig?: (customFieldsConfig: CustomFieldTemplateApi[] | undefined) => void,
	addFlash?: (_: Flash) => void
) => {
	if (!addFlash || !setCustomFieldsConfig) return undefined;

	try {
		const params: Record<string, string> = {
			f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=EMPLOYEE`),
			include: "options,constraints",
		};
		const res = await api.organization().getCustomFields(params);
		setCustomFieldsConfig(res);
	} catch (err) {
		handleError.alert(err, addFlash);
	}
};

export const handleAddEmployeeBasedOnSubscription = async (
	organization: OrganizationState,
	handleSetSubscriptionBasedRestrictionModalConfig: (config: SubscriptionBasedModalConfigProps) => void,
	t: TFunction,
	handleShowSubscriptionBasedRestrictionModal: () => void,
	callback?: () => void | Promise<void>,
	onHide?: () => void,
	forceSkipSubscriptionBasedRescritions?: boolean
) => {
	const isLicenseTypeManual = organization.license_type === "MANUAL";

	if (!getSubscriptionBasedRestrictions() || isLicenseTypeManual) {
		if (callback) await callback();
		return;
	}

	const hiredEmployeesFilter = "status|e=HIRED";
	const encodedFilter = btoa(unescape(encodeURIComponent(hiredEmployeesFilter)));
	const params: Record<string, any> = { f: encodedFilter };
	const employeeAmount = await apiOrganization.getEmployeeCount(params);

	if (employeeAmount === undefined || employeeAmount === null) {
		if (callback) await callback();
		return;
	}

	if (
		Number(employeeAmount) >= Number(organization.accesses?.["employee-limit"]) &&
		!forceSkipSubscriptionBasedRescritions
	) {
		handleSetSubscriptionBasedRestrictionModalConfig({
			featureId: "employee-limit",
			featureName: t("enums.subscription_restriction.feature_name.employee_limit"),
			limit: Number(organization.accesses?.["employee-limit"]),
			property: Number(employeeAmount),
			resource: t("enums.message_events.resource_type.EMPLOYEE"),
			featureTranslations: [
				{
					id: "user-licenses",
					name: t("enums.subscription_restriction.feature_name.user_licenses"),
				},
				{
					id: "employee-limit",
					name: t("enums.subscription_restriction.feature_name.employee_limit"),
				},
				{
					id: "reservation",
					name: t("enums.subscription_restriction.feature_name.reservation"),
				},
			],
			companyId: organization.company_id ?? "",
			accountsUrl: getGoAccountsApiURL(),
			getSubscriptionsEstimate: (featureId, params, options) =>
				apiSecurityOrganization.getSubscriptionsEstimate(featureId, params, options),
			onHide,
		});
		handleShowSubscriptionBasedRestrictionModal();
	} else if (callback) await callback();
};
